import { BackupsApi } from 'backups/api/BackupsApi';
import { CheckpointUtils } from 'backups/modules/CheckpointModule/CheckpointModule.utils';
import { InfraConstants } from 'infra/infraConstants';
import * as R from 'ramda';
import { metrics } from 'system/libs/metrics';
import { arrayHelper } from 'utils/arrayHelper';
import { ModuleHelper } from 'utils/moduleHelper';
import { ModuleListReloader } from 'utils/ModuleListReloader/ModuleListReloader';
import { reduxHelper } from 'utils/reduxHelper';
import { stateHelper } from 'utils/stateHelper';

export const CHECKPOINT_STATUSES = {
    error: 'error',
    protecting: 'protecting',
    available: 'available',
    deleting: 'deleting',
    deleted: 'deleted',
    error_deleting: 'error-deleting',
};

export const CHECKPOINT_STATUS_TO_PARAMS = {
    [CHECKPOINT_STATUSES.error]: {
        color: 'red',
        inProgress: false,
        status: 'Создан с ошибкой',
    },
    [CHECKPOINT_STATUSES.protecting]: {
        color: 'green',
        inProgress: true,
        status: 'Создание',
    },
    [CHECKPOINT_STATUSES.available]: {
        color: 'green',
        inProgress: false,
        status: '',
    },
    [CHECKPOINT_STATUSES.deleting]: {
        color: 'red',
        inProgress: true,
        status: 'Удаление',
    },
    [CHECKPOINT_STATUSES.deleted]: {
        color: 'red',
        inProgress: false,
        status: 'Удален',
    },
    [CHECKPOINT_STATUSES.error_deleting]: {
        color: 'red',
        inProgress: false,
        status: 'При удалении возникла ошибка',
    },
};

const getModule = ModuleHelper.wrapMultimodule((service) => {
    const module = stateHelper.getMultiModule({
        name: 'checkpoints',
        key: service,
        initialState: {
            list: [],
            isLoaded: false,
            isLoading: false,
            hasError: false,
        },
    });

    const initialLoadList = reduxHelper.createLoadListAction({
        module,
        apiMethod: BackupsApi.loadCheckpoints(service),
        entityName: 'manual checkpoints',
    });

    const loadList = reduxHelper.createUpdateListAction({
        module,
        apiMethod: BackupsApi.loadCheckpoints(service),
        entityName: 'manual checkpoints',
        entityNameLocal: 'список чекпоинтов',
    });

    const loadListSavingState = reduxHelper.createLoadListSavingStateAction({
        loadList: initialLoadList,
        updateList: loadList,
        module,
    });

    const {
        getList,
        getIsLoaded,
        getIsLoading,
        getHasError,
        getItem: getCheckpoint,
    } = reduxHelper.makeStandardSelectors(module);

    const { addConsumer, removeConsumer } = new ModuleListReloader({
        loadList,
        initialLoadList,
        selectIsLoaded: getIsLoaded,
    });

    const createCheckpoint = reduxHelper.makeCreateAction({
        module,
        apiMethod: BackupsApi.createCheckpoint(service),
        entityName: 'checkpoint',
        entityNamePublic: 'бэкап',
    });

    const removeCheckpoint = (id) => async (dispatch, getState) => {
        const setDeleting = () => {
            const list = getList(getState());
            const originalItem = getCheckpoint(getState(), id);

            const item = R.evolve(
                { status: () => CHECKPOINT_STATUSES.deleting },
                originalItem,
            );

            dispatch(
                module.setPart(
                    'list',
                    arrayHelper.getListWithInsertedItem({
                        list,
                        item,
                    }),
                ),
            );
        };

        try {
            await BackupsApi.removeCheckpoint(service, id);

            setDeleting();
        } catch (error) {
            console.error('checkpoint removing error', error);

            throw 'При удалении точки восстановления произошла ошибка';
        }
    };

    const removeIncrementalCheckpoint = (checkpointId, plan) => async (
        dispatch,
        getState,
    ) => {
        try {
            const checkpointsByPlan = getCheckpointsByPlan(getState(), plan.id);
            const checkpointsForRemove = CheckpointUtils.getCheckpointsForRemove(
                {
                    checkpointsByPlan,
                    checkpointId,
                },
            );

            const idsForRemove = checkpointsForRemove.map(
                (checkpoint) => checkpoint.id,
            );
            const { addConsumer, removeConsumer } = new ModuleListReloader({
                loadList,
                initialLoadList,
                selectIsLoaded: getIsLoaded,
                updateTimeout: InfraConstants.ITEM_UPDATE_INTERVAL,
            });
            idsForRemove.forEach((id) => {
                metrics.send({
                    action: 'RemoveBackupStart',
                    category: id,
                });
            });
            for (const id of idsForRemove) {
                await BackupsApi.removeCheckpoint(service, id);
                await dispatch(addConsumer());
                await new Promise((resolve, reject) => {
                    let countTryRemove = 0;
                    const maxCountTryRemove = 10;
                    // инкрементальные чекпоинты удаляются последовательно, поэтому тут ждем полного удаления чекпоинта
                    const intervalId = setInterval(() => {
                        countTryRemove++;
                        if (countTryRemove === maxCountTryRemove) {
                            reject(
                                new Error(
                                    'не удалось удалить точку восстановления ' +
                                        id,
                                ),
                            );
                        }
                        const checkpoint = getCheckpoint(getState(), id);
                        if (!checkpoint) {
                            metrics.send({
                                action: 'RemoveBackupFinish',
                                category: id,
                            });
                            clearInterval(intervalId);
                            resolve();
                        }
                    }, InfraConstants.ITEM_UPDATE_INTERVAL);
                });
                await dispatch(removeConsumer());
            }
        } catch (error) {
            console.error('checkpoint removing error', error);

            throw 'При удалении точки восстановления произошла ошибка';
        }
    };

    const loadCheckpoint = reduxHelper.createLoadItemAction({
        module,
        apiMethod: BackupsApi.loadCheckpoint,
        entityName: 'checkpoint',
        entityNamePublic: 'чекпоинт',
    });
    const getCheckpointsByPlan = (state, planId) => {
        const checkpoints = getList(state);
        const checkpointsByPlan = checkpoints.filter((checkpoint) =>
            R.pathEq(['protection_plan', 'id'], planId)(checkpoint),
        );

        const hasIncrementalCheckpoints = checkpointsByPlan.some(
            (checkpoint) => checkpoint.extra_info.backup_mode === 'full',
        );

        if (!hasIncrementalCheckpoints) {
            return checkpointsByPlan;
        }

        return CheckpointUtils.getCheckpointsWithIncremental(checkpointsByPlan);
    };

    const getCheckpointResources = (state, id) =>
        R.pipe(
            getCheckpoint,
            R.pathOr([], ['protection_plan', 'resources']),
            R.filter(R.propEq('type', 'OS::Nova::Server')),
        )(state, id);

    const removeFewCheckpoints = reduxHelper.multiplyAction(removeCheckpoint);
    const removeIncrementalCheckpoints = reduxHelper.multiplyAction(
        removeIncrementalCheckpoint,
    );

    const Selectors = {
        getModuleState: module.getModuleState,
        getList,
        getIsLoaded,
        getIsLoading,
        getHasError,
        getCheckpoint,
        getCheckpointsByPlan,
        getCheckpointResources,
    };

    const Actions = {
        addConsumer,
        removeConsumer,
        removeFewCheckpoints,
        removeIncrementalCheckpoints,
        createCheckpoint,
        loadListSavingState,
        loadCheckpoint,
    };

    return {
        Selectors,
        Actions,
    };
});

const getCheckpointSelectors = (service) => getModule(service).Selectors;
const getCheckpointActions = (service) => getModule(service).Actions;

export const CheckpointSelectors = getCheckpointSelectors('NOVA');
export const CheckpointActions = getCheckpointActions('NOVA');

export const DatabaseCheckpointSelectors = getCheckpointSelectors('TROVE');
export const DatabaseCheckpointActions = getCheckpointActions('TROVE');
